import { useCallback, useEffect, useState } from "react";
import Image from "next/legacy/image";
import styles from "../../styles/case-studies/CaseStudies.module.css";

interface GalleryImage {
  src: string;
  width: number;
  height: number;
  alt: string;
}

interface CaseGalleryProps {
  title: string;
  images: GalleryImage[];
  note?: string;
  enlargeLabel: string;
  closeLabel: string;
  prevLabel: string;
  nextLabel: string;
}

const CaseGallery = ({
  title,
  images,
  note,
  enlargeLabel,
  closeLabel,
  prevLabel,
  nextLabel
}: CaseGalleryProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isOpen = activeIndex !== null;
  const hasMultiple = images.length > 1;

  const close = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(
    () => setActiveIndex((index) => (index === null ? index : (index - 1 + images.length) % images.length)),
    [images.length]
  );
  const showNext = useCallback(
    () => setActiveIndex((index) => (index === null ? index : (index + 1) % images.length)),
    [images.length]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      } else if (event.key === "ArrowLeft") {
        showPrev();
      } else if (event.key === "ArrowRight") {
        showNext();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [isOpen, close, showPrev, showNext]);

  const activeImage = activeIndex === null ? null : images[activeIndex];

  return (
    <div className={styles.block}>
      <h3 className={styles.blockTitle}>{title}</h3>
      <div className={styles.gallery}>
        {images.map((image, index) => (
          <figure key={image.src} className={styles.galleryItem}>
            <button
              type="button"
              className={styles.galleryTrigger}
              onClick={() => setActiveIndex(index)}
              aria-label={`${enlargeLabel}: ${image.alt}`}
            >
              <span className={styles.galleryMedia}>
                <Image
                  src={image.src}
                  layout="fill"
                  objectFit="cover"
                  objectPosition="top center"
                  sizes="(max-width: 1023px) 100vw, 50rem"
                  alt={image.alt}
                />
              </span>
              <span className={styles.galleryZoom} aria-hidden="true">
                ⤢
              </span>
            </button>
            <figcaption className={styles.galleryCaption}>{image.alt}</figcaption>
          </figure>
        ))}
      </div>
      {note && <p className={styles.galleryNote}>{note}</p>}

      {activeImage && (
        <div
          className={styles.lightboxBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label={activeImage.alt}
          onClick={close}
        >
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={close}
            aria-label={closeLabel}
          >
            ×
          </button>

          {hasMultiple && (
            <button
              type="button"
              className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
              onClick={(event) => {
                event.stopPropagation();
                showPrev();
              }}
              aria-label={prevLabel}
            >
              ‹
            </button>
          )}

          <figure
            className={styles.lightboxFigure}
            onClick={(event) => event.stopPropagation()}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={activeImage.src}
              alt={activeImage.alt}
              className={styles.lightboxImage}
              width={activeImage.width}
              height={activeImage.height}
            />
            <figcaption className={styles.lightboxCaption}>{activeImage.alt}</figcaption>
          </figure>

          {hasMultiple && (
            <button
              type="button"
              className={`${styles.lightboxNav} ${styles.lightboxNext}`}
              onClick={(event) => {
                event.stopPropagation();
                showNext();
              }}
              aria-label={nextLabel}
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseGallery;
